import { useCallback, useMemo, useSyncExternalStore } from 'react'
import useSWR from 'swr'

import { knowledgeApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'

import type { AssistantEffort } from '@digital-twins/shared-types'
import type { AssistantProfileOption } from '@/lib/api'

/**
 * The Assistant profile selection (plan 2026-10-21 assistant insights capabilities, Step A2).
 *
 * ONE selection shared by every Assistant composer (Home Lab Ask and the thread composer), so
 * a pick made on Home is the pick the thread's later turns send, and it survives a reload.
 * The stored value is only a preference: the effective profile is the stored id when the
 * server still lists it for this viewer, else the registry default. A stored staff id is
 * therefore ignored for a viewer who is not verified staff, the same way the server refuses
 * it. Until the list loads (or when it fails) there is no effective profile and the turn is
 * sent without one, which keeps today's request.
 *
 * The list comes from `GET /knowledge/assistant/profiles`, which reads the one registry
 * (`config/assistant-profiles.yaml`); nothing here mirrors it.
 */

export const ASSISTANT_PROFILE_STORAGE_KEY = 'fairgen.assistant.profile'

const listeners = new Set<() => void>()
let memoryValue: string | null | undefined

function readStoredProfile(): string | null {
  if (memoryValue !== undefined) {
    return memoryValue
  }
  try {
    memoryValue = window.localStorage.getItem(ASSISTANT_PROFILE_STORAGE_KEY)
  } catch {
    memoryValue = null
  }
  return memoryValue
}

/** Records a pick for every mounted composer and for the next visit. */
export function storeAssistantProfile(profileId: string): void {
  memoryValue = profileId
  try {
    window.localStorage.setItem(ASSISTANT_PROFILE_STORAGE_KEY, profileId)
  } catch {
    // Storage blocked: the pick still holds for this session.
  }
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam: forget the in-memory pick so the next read goes back to storage. */
export function resetAssistantProfileMemoryForTests(): void {
  memoryValue = undefined
}

/** The effective profile: the stored id when listed for this viewer, else the default. */
export function effectiveAssistantProfileId(
  options: Array<AssistantProfileOption> | null,
  defaultProfileId: string | null,
  storedProfileId: string | null,
): string | null {
  if (!options || options.length === 0) {
    return null
  }
  if (storedProfileId && options.some((option) => option.id === storedProfileId)) {
    return storedProfileId
  }
  if (defaultProfileId && options.some((option) => option.id === defaultProfileId)) {
    return defaultProfileId
  }
  return null
}

/**
 * What `ContextRing`'s effort line shows: a public profile's own level (low, medium, high),
 * else the legacy effort the turn would send. A staff profile keeps the legacy value.
 */
export function contextRingEffort(profileId: string | null, fallback: AssistantEffort): AssistantEffort {
  if (profileId === 'low' || profileId === 'medium' || profileId === 'high') {
    return profileId
  }
  return fallback
}

export interface UseAssistantProfileResult {
  /** Profiles this viewer may pick, registry order. Null until loaded. */
  options: Array<AssistantProfileOption> | null
  /** The profile the next turn sends, or null (no list yet: the turn sends none). */
  profileId: string | null
  setProfileId: (profileId: string) => void
}

export function useAssistantProfile(isEnabled: boolean): UseAssistantProfileResult {
  const { user } = useAuth()

  const storedProfileId = useSyncExternalStore(subscribe, readStoredProfile, () => null)

  /* Keyed by viewer: a staff list must never be served to the next account on this tab. */
  const { data } = useSWR(
    isEnabled && user ? ['knowledge/assistant/profiles', user.id] : null,
    () => knowledgeApi.listAssistantProfiles(),
    { revalidateOnFocus: false },
  )

  /* Credit metering: the typical cost per message is shown only when the org is enrolled
     (`on`). Off and shadow accounts see today's picker, so the server's pricing is dropped. */
  const isEnrolled = user?.creditInfo?.meteringMode === 'on'
  const options = useMemo(() => {
    const profiles = data?.profiles ?? null
    if (!profiles || isEnrolled) {
      return profiles
    }
    return profiles.map(({ pricing: _pricing, ...option }) => option)
  }, [data?.profiles, isEnrolled])
  const profileId = effectiveAssistantProfileId(options, data?.defaultProfileId ?? null, storedProfileId)

  const setProfileId = useCallback((next: string) => {
    storeAssistantProfile(next)
  }, [])

  return { options, profileId, setProfileId }
}
